import React, { Children, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
    LandingPageWrapperProps,
    MenuOptionsType,
    styles,
} from './landing-page-wrapper-helper';
import { Fab, Menu, MenuItem, Select } from '@biq/apex-ui';
import AddIcon from '@mui/icons-material/Add';
import { HelixLoader } from 'components/helix-loader/HelixLoader';
import { CustomIconButton } from 'components/custom-icon-button/CustomIconButton';
import { TabsAndSelectFieldComponent } from './components/TabsAndSelectFieldComponent';
import { TableComponent } from './components/TableComponent';

export const LandingPageWrapper = ({
    title,
    showAddButton,
    onAddButtonClick,
    showMenuButton,
    openMenuItems,
    menuOptions,
    menuStyles,
    handleMenuButtonClick,
    showRefreshButton,
    onRefreshButtonClick,
    tableData,
    isLoading,
    columns,
    getRowId,
    onRowClick,
    paginationPageSizeOptions,
    paginationPageSize,
    emptyStateIcon,
    emptyStateHeading,
    emptyStateBodyMessage,
    customImageStyles,
    customHeadingContainerStyles,
    selectedTab,
    onTabChange,
    tabItems = [],
    tabsContainerStyles,
    tabsStyles,
    tabItemStyles,
    tableHeight = 210,
    rowHeight = 52,
    autoRowHeight = false,
    readonly = false,
    readonlyTooltipTitle = '',
    isSelectFieldEnabled = false,
    selectedChain,
    isTabAndTableComponentVisible = true,
    isSelectFilterVisible = false,
    selectFilterValue,
    selectFilterStyles,
    selectFilterChildren,
    sortModel,
    children,
    rowCount,
    paginationMode,
    onPaginationModelChange,
    paginationModel,
    handleStateChange,
    filterModel,
    onFilterModelChange,
    setSelectedChain,
}: LandingPageWrapperProps): React.JSX.Element => {
    const maxGridHeight = `calc(100vh - ${tableHeight}px)`;
    const isGridHeightAuto: boolean = rowHeight > 52;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    const gridHeight = Math.min(
        tableData?.length * 52 + 113,
        window.innerHeight - tableHeight
    );

    if (isLoading) {
        return <HelixLoader />;
    }

    const onAddBtnClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ): void => {
        if (showMenuButton) {
            handleMenuButtonClick?.();
            if (!openMenuItems) return;
            setAnchorEl(event.currentTarget);
        } else {
            onAddButtonClick?.();
        }
    };

    const handleMenuClose = (): void => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (option: MenuOptionsType): void => {
        option.onClick();
        handleMenuClose();
    };

    return (
        <Grid sx={styles.mainContainer}>
            <Grid sx={styles.titleAndAddButtonContainer}>
                <Grid sx={styles.titleContainer}>
                    <Typography sx={styles.headingText}>{title}</Typography>
                    {showRefreshButton && (
                        <CustomIconButton
                            className='refresh-icon'
                            title='Refresh Page'
                            icon='ic:baseline-refresh'
                            onClick={onRefreshButtonClick || ((): void => {})}
                        />
                    )}
                </Grid>
                {showAddButton && (
                    <Grid>
                        {isSelectFilterVisible && (
                            <Select
                                value={selectFilterValue}
                                sx={selectFilterStyles}
                            >
                                {selectFilterChildren}
                            </Select>
                        )}
                        <Fab
                            type='button'
                            sx={styles.addButtonStyles}
                            icon={<AddIcon />}
                            color='secondary'
                            label='New'
                            onClick={onAddBtnClick}
                        />
                        {openMenuItems && (
                            <Menu
                                anchorEl={anchorEl}
                                anchorOrigin={menuStyles.anchorOrigin}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                open={openMenu}
                                onClose={handleMenuClose}
                                sx={styles.menuStyles}
                            >
                                {Children.toArray(
                                    menuOptions?.map(
                                        (option: MenuOptionsType) => (
                                            <MenuItem
                                                onClick={(): void =>
                                                    handleMenuItemClick(option)
                                                }
                                            >
                                                <Box sx={styles.menuItemStyles}>
                                                    <img
                                                        src={option.icon}
                                                        alt={option.label}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            ...styles.menuItemTextStyles,
                                                        }}
                                                    >
                                                        {option.label}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        )
                                    )
                                )}
                            </Menu>
                        )}
                    </Grid>
                )}
            </Grid>
            {isTabAndTableComponentVisible ? (
                <>
                    {tabItems?.length > 0 && (
                        <TabsAndSelectFieldComponent
                            tabItems={tabItems}
                            selectedTab={selectedTab}
                            onTabChange={onTabChange || ((): void => {})}
                            tabsContainerStyles={tabsContainerStyles}
                            tabsStyles={tabsStyles}
                            tabItemStyles={tabItemStyles}
                            isSelectFieldEnabled={isSelectFieldEnabled}
                            selectedChain={selectedChain}
                            setSelectedChain={setSelectedChain}
                        />
                    )}
                    <TableComponent
                        tableData={tableData}
                        gridHeight={gridHeight}
                        maxGridHeight={maxGridHeight}
                        tableHeight={tableHeight}
                        autoRowHeight={autoRowHeight}
                        isGridHeightAuto={isGridHeightAuto}
                        columns={columns}
                        getRowId={getRowId}
                        onRowClick={onRowClick}
                        paginationpageSizeOptions={paginationPageSizeOptions}
                        paginationPageSize={paginationPageSize}
                        emptyStateIcon={emptyStateIcon}
                        emptyStateHeading={emptyStateHeading}
                        emptyStateBodyMessage={emptyStateBodyMessage}
                        readonly={readonly}
                        readonlyTooltipTitle={readonlyTooltipTitle}
                        customImageStyles={customImageStyles}
                        customHeadingContainerStyles={
                            customHeadingContainerStyles
                        }
                        sortModel={sortModel}
                        rowCount={rowCount}
                        paginationMode={paginationMode}
                        onPaginationModelChange={onPaginationModelChange}
                        paginationModel={paginationModel}
                        handleStateChange={handleStateChange}
                        filterModel={filterModel}
                        onFilterModelChange={onFilterModelChange}
                    />
                </>
            ) : (
                children
            )}
        </Grid>
    );
};
